import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { KeycloakAuthGuard, KeycloakService } from 'keycloak-angular';
import { UserContext } from '@base/services/user-context';

@Injectable({
  providedIn: 'root',
})
export class KeycloakCanActivateGuard extends KeycloakAuthGuard {

  constructor(protected readonly router: Router,
              private userContext: UserContext,
              protected readonly keycloak: KeycloakService) {
    super(router, keycloak);
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    // Wait for parameters to be loaded
    const parameters = this.userContext.parameters;

    // If parameters are not loaded yet or keycloakEnabled is false, allow access
    if (!parameters || !parameters.keycloakEnabled) {
      return Promise.resolve(true);
    }

    try {
      // Check if Keycloak is initialized
      if (!this.keycloak.getKeycloakInstance()) {
        console.warn('Keycloak not initialized yet, allowing route');
        return true;
      }

      return await super.canActivate(route, state);
    } catch (error) {
      console.error('Error in KeycloakCanActivateGuard:', error);
      return true; // Allow access if there's an error to prevent infinite loops
    }
  }

  async isAccessAllowed(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    try {
      // Double-check if Keycloak is enabled at this point
      const parameters = this.userContext.parameters;
      if (!parameters || !parameters.keycloakEnabled) {
        return true;
      }

      // Wait for Keycloak to be ready
      const isLoggedIn = await this.keycloak.isLoggedIn();

      if (!isLoggedIn) {
        // Only trigger login if we're not already in a login flow
        const currentUrl = window.location.href;
        if (!currentUrl.includes('code=') && !currentUrl.includes('error=') && !currentUrl.includes('state=')) {
          await this.keycloak.login({
            redirectUri: window.location.origin + state.url,
          });
        }
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking authentication:', error);
      // If there's an error, allow access to prevent infinite loops
      return true;
    }
  }
}
